/**
 * Auth host login redirect for handshake (adapted from prequal-ui).
 *
 * Unlike prequal we do **not** collect the phone number in PPUI — Auth owns
 * phone + OTP. After OTP, Auth sets `tamara-checkout-token` on `.tamara.co`
 * and sends the browser to `redirect_url`.
 *
 * Shape:
 *   {VITE_AUTH_LOGIN_URL}?client_type=CHECKOUT&redirect_url={checkoutOrigin}/?checkoutId=…
 *
 * Auth extracts `checkoutId` from the redirect_url query (not only from
 * `/checkout/{id}` path forms).
 */

export const AUTH_CLIENT_TYPE = "CHECKOUT";

export interface AuthLoginRedirectParams {
  checkoutId: string;
  /** Optional extras to forward on redirect_url (e.g. locale). */
  search?: string;
  authLoginUrl?: string;
  checkoutRedirectBaseUrl?: string;
}

function requireEnv(name: string, value: string | undefined): string {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) {
    throw new Error(`[handshake] ${name} is not configured`);
  }
  return trimmed;
}

/**
 * Builds the post-auth return URL Auth will bounce to after login.
 * e.g. https://checkout-staging.tamara.co/?checkoutId=abc
 *     https://checkout.tamara.co/v2/?checkoutId=abc  (prod, temporary /v2 base)
 */
export function buildCheckoutAuthRedirectUrl({
  checkoutId,
  search = "",
  checkoutRedirectBaseUrl = import.meta.env.VITE_AUTH_REDIRECT_BASE_URL,
}: Omit<AuthLoginRedirectParams, "authLoginUrl">): string {
  const base = requireEnv(
    "VITE_AUTH_REDIRECT_BASE_URL",
    checkoutRedirectBaseUrl,
  );
  const redirectUrl = new URL(base.endsWith("/") ? base : `${base}/`);
  const fromSearch = new URLSearchParams(search);
  // Canonical id wins over any stale query value.
  redirectUrl.searchParams.set("checkoutId", checkoutId);
  const locale = fromSearch.get("locale");
  if (locale) redirectUrl.searchParams.set("locale", locale);
  return redirectUrl.toString();
}

/**
 * Builds the Tamara Auth login URL for an unauthenticated handshake exit.
 */
export function buildAuthLoginUrl({
  checkoutId,
  search = "",
  authLoginUrl = import.meta.env.VITE_AUTH_LOGIN_URL,
  checkoutRedirectBaseUrl = import.meta.env.VITE_AUTH_REDIRECT_BASE_URL,
}: AuthLoginRedirectParams): string {
  const loginBase = requireEnv("VITE_AUTH_LOGIN_URL", authLoginUrl);
  const loginUrl = new URL(loginBase);
  loginUrl.searchParams.set("client_type", AUTH_CLIENT_TYPE);
  loginUrl.searchParams.set(
    "redirect_url",
    buildCheckoutAuthRedirectUrl({
      checkoutId,
      search,
      checkoutRedirectBaseUrl,
    }),
  );
  return loginUrl.toString();
}
